// IA DE VOZ da organização: tudo que vive sob /v1/voice/*. O recorte é esse
// prefixo de rota, mas o que de fato mantém as três metades juntas é o PAYLOAD
// do /session — o pré-registro que o back monta a cada ligação com IA:
//
//   - o ROTEIRO (/v1/voice/roteiros/*): o que a IA fala. Mora no LAI Compass
//     (construtor visual); o back é BFF, injeta a API key e escopa pela marca;
//   - a VOZ (/v1/voice/catalog, /v1/voice/voices/selection): como ela soa. O
//     catálogo é enumerado ao vivo no provedor (o talk é stateless, #103) e a
//     org escolhe uma spec, que vai no `/session.voice`;
//   - o NOME (/v1/voice/voices/attendant-name): quem ela diz que é. Vai no
//     `/session.variaveis.executivo_nome`.
//
// Some-se `lead-fields` (por roteiro, alimenta `/session.lead.dados`) e o
// próprio `/v1/voice/call/session`, que é o ticket de WebSocket da emulação —
// literalmente uma sessão dessas, aberta à mão pela tela. Três módulos aqui
// seriam três recortes do MESMO objeto, e o comentário de cada função teria de
// citar os outros dois para se explicar.
//
// Ficam de FORA os dois toggles que nasceram no meio desta seção em api.ts e
// eram /v1/sip/* (ai-enrich e click-to-call): foram para ./sip.js na mesma onda
// em que este arquivo nasceu — o recorte daquele módulo é o prefixo /v1/sip, e
// nenhum dos dois fala da IA de voz (um é enriquecimento do lead no HubSpot ao
// discar, o outro liga um GRUPO INTEIRO do menu).
//
// O back injeta o Bearer do talk server-side; o front só fala com o próprio
// back. O `doc` do roteiro é o ScriptDoc do pacote @script-builder — forma opaca
// aqui (validada no construtor), tipada como `unknown` para não duplicar o
// schema. A org já é resolvida no back pela sessão; o front não manda X-Org-Id.
import i18n from "../i18n.js";
import { api, API_BASE_URL, apiUpload, toApiError } from "./client.js";

// Voz do provedor (voice-talk stateless #103): o talk não guarda mais vozes —
// enumera o catálogo do provedor ao vivo. Um "modelo" agrupa vozes.
export interface VoiceCatalogModel {
  id: string;
  nome: string;
  voices: string[];
  voice_names?: Record<string, string>;
  pt_br?: boolean | null;
  pricing?: unknown;
  descricao?: string;
}
export interface VoiceCatalog {
  provider: string;
  modelos: VoiceCatalogModel[];
}

// Spec da voz do provedor — o que a org escolhe e o que vai no /session.voice.
export interface VoiceSpec {
  provider: string;
  voice_id: string;
  model?: string;
  lang?: string;
  settings?: Record<string, unknown>;
}

export const VOICE_PROVIDERS = ["elevenlabs", "openrouter"] as const;
export type VoiceProvider = (typeof VOICE_PROVIDERS)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Roteiros da IA de voz no LAI Compass (construtor visual) — /v1/voice/roteiros/*.
// O Compass é a fonte da verdade do roteiro; o back é BFF (injeta a API key e
// escopa pela marca).
// ─────────────────────────────────────────────────────────────────────────────

export interface RoteiroResumo {
  id: string;
  orgId: string | null;
  orgNome: string; // a MARCA do roteiro — a tela mostra e agrupa por ela
  nome: string;
  descricao: string | null;
  ativo: boolean; // o vigente da org (o que o voice-talk executa na ligação)
  versaoPublicada: number | null;
  publicadoEm: string | null;
  totalFases: number;
  createdAt: string;
  updatedAt: string;
}
export interface RoteiroPage {
  data: RoteiroResumo[];
  total: number;
  limit: number;
  offset: number;
}
export interface RoteiroCompleto extends RoteiroResumo {
  doc: unknown; // ScriptDoc (contrato do @script-builder)
  yaml: string;
  sourceYaml: string | null;
}

export async function getRoteiros(): Promise<RoteiroPage> {
  const { data, error } = await api<RoteiroPage>("/v1/voice/roteiros");
  if (error) throw toApiError("GET /v1/voice/roteiros", error);
  return data;
}

export async function getRoteiro(id: string): Promise<RoteiroCompleto> {
  const { data, error } = await api<RoteiroCompleto>(`/v1/voice/roteiros/${id}`);
  if (error) throw toApiError(`GET /v1/voice/roteiros/${id}`, error);
  return data;
}

export async function createRoteiro(input: {
  nome: string;
  descricao?: string;
  doc?: unknown;
  sourceYaml?: string;
}): Promise<RoteiroCompleto> {
  const { data, error } = await api<RoteiroCompleto>("/v1/voice/roteiros", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/voice/roteiros", error);
  return data;
}

export async function saveRoteiro(
  id: string,
  input: { nome?: string; doc?: unknown; yaml?: string; expectedUpdatedAt?: string },
): Promise<RoteiroCompleto> {
  const { data, error } = await api<RoteiroCompleto>(`/v1/voice/roteiros/${id}`, {
    method: "PATCH",
    body: input,
  });
  if (error) throw toApiError(`PATCH /v1/voice/roteiros/${id}`, error);
  return data;
}

export async function publishRoteiro(
  id: string,
  // `doc` é obrigatório: o Compass reidrata e valida o ScriptDoc na publicação
  // (o `yaml` é só o que o voice-talk executa). Sem `doc` o PUT do Compass cai
  // na validação de schema e devolve um 422 genérico "Validation failed".
  input: { doc: unknown; yaml: string; nota?: string },
): Promise<RoteiroCompleto> {
  const { data, error } = await api<RoteiroCompleto>(`/v1/voice/roteiros/${id}/publish`, {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError(`POST /v1/voice/roteiros/${id}/publish`, error);
  return data;
}

// Elege o roteiro vigente da org (o Compass rebaixa o anterior na mesma transação).
export async function activateRoteiro(id: string): Promise<RoteiroCompleto> {
  const { data, error } = await api<RoteiroCompleto>(`/v1/voice/roteiros/${id}/activate`, {
    method: "POST",
  });
  if (error) throw toApiError(`POST /v1/voice/roteiros/${id}/activate`, error);
  return data;
}

// GET/PUT /v1/voice/roteiros/:nome/lead-fields — liga/desliga por roteiro dos
// campos do lead que vão no lead.dados do /session (opt-in; estado do Disk,
// não vai ao Compass). disk#94 (incremento).
export async function getRoteiroLeadFields(id: string): Promise<{ fields: string[] }> {
  const { data, error } = await api<{ fields: string[] }>(`/v1/voice/roteiros/${id}/lead-fields`);
  if (error) throw toApiError(`GET /v1/voice/roteiros/${id}/lead-fields`, error);
  return data;
}

export async function setRoteiroLeadFields(
  id: string,
  fields: string[],
): Promise<{ fields: string[] }> {
  const { data, error } = await api<{ fields: string[] }>(`/v1/voice/roteiros/${id}/lead-fields`, {
    method: "PUT",
    body: { fields },
  });
  if (error) throw toApiError(`PUT /v1/voice/roteiros/${id}/lead-fields`, error);
  return data;
}

export async function removeRoteiro(id: string): Promise<RoteiroCompleto> {
  const { data, error } = await api<RoteiroCompleto>(`/v1/voice/roteiros/${id}`, {
    method: "DELETE",
  });
  if (error) throw toApiError(`DELETE /v1/voice/roteiros/${id}`, error);
  return data;
}

// POST /v1/voice/roteiros/generate — sobe o PDF; o voice-talk gera e RETORNA o
// YAML do roteiro (stateless, não grava). O front converte YAML→doc e publica no
// Compass pelo fluxo de roteiro (createRoteiro). disk#91 / voice-talk#111.
export async function generateRoteiroPdf(input: {
  file: File;
}): Promise<{ yaml: string; dados: Record<string, unknown> }> {
  const form = new FormData();
  form.append("file", input.file);
  return apiUpload("/v1/voice/roteiros/generate", form);
}

// POST /v1/voice/roteiros/generate-prompt — gera o YAML a partir de um texto.
export async function generateRoteiroPrompt(
  prompt: string,
): Promise<{ yaml: string; dados: Record<string, unknown> }> {
  const { data, error } = await api<{ yaml: string; dados: Record<string, unknown> }>(
    "/v1/voice/roteiros/generate-prompt",
    { method: "POST", body: { prompt } },
  );
  if (error) throw toApiError("POST /v1/voice/roteiros/generate-prompt", error);
  return data;
}

// GET /v1/voice/catalog?provider= — catálogo ao vivo do provedor (o talk não
// guarda mais vozes): modelos + vozes disponíveis para montar a tela de escolha.
export async function getVoiceCatalog(provider: string): Promise<VoiceCatalog> {
  const { data, error } = await api<VoiceCatalog>("/v1/voice/catalog", { query: { provider } });
  if (error) throw toApiError("GET /v1/voice/catalog", error);
  return data;
}

// GET /v1/voice/voices/selection — a voz (spec) escolhida pela org, ou null.
export async function getVoiceSelection(): Promise<{ selecionada: VoiceSpec | null }> {
  const { data, error } = await api<{ selecionada: VoiceSpec | null }>(
    "/v1/voice/voices/selection",
  );
  if (error) throw toApiError("GET /v1/voice/voices/selection", error);
  return data;
}

// POST /v1/voice/voices/selection — a org escolhe a voz (spec do provedor). Vale
// nas ligações via o /session.voice do pré-registro.
export async function setVoiceSelection(
  spec: VoiceSpec,
): Promise<{ ok: boolean; selecionada: VoiceSpec }> {
  const { data, error } = await api<{ ok: boolean; selecionada: VoiceSpec }>(
    "/v1/voice/voices/selection",
    { method: "POST", body: spec },
  );
  if (error) throw toApiError("POST /v1/voice/voices/selection", error);
  return data;
}

// GET /v1/voice/voices/attendant-name — nome com que a IA se apresenta na ligação
// (persona por org). Injetado como variaveis.executivo_nome no /session.
export async function getAttendantName(): Promise<{ nome: string | null }> {
  const { data, error } = await api<{ nome: string | null }>("/v1/voice/voices/attendant-name");
  if (error) throw toApiError("GET /v1/voice/voices/attendant-name", error);
  return data;
}

// PATCH /v1/voice/voices/attendant-name — define/limpa o nome (vazio limpa).
export async function setAttendantName(
  nome: string,
): Promise<{ ok: boolean; nome: string | null }> {
  const { data, error } = await api<{ ok: boolean; nome: string | null }>(
    "/v1/voice/voices/attendant-name",
    { method: "PATCH", body: { nome } },
  );
  if (error) throw toApiError("PATCH /v1/voice/voices/attendant-name", error);
  return data;
}

// POST /v1/voice/preview — amostra de áudio (WAV binário) de uma spec de voz.
// Fetch cru com credentials porque é binário; devolve um Blob para tocar via <audio>.
export async function previewVoice(spec: VoiceSpec & { texto?: string }): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}/v1/voice/preview`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(spec),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const b = (await res.json()) as { error?: string; detail?: string };
      detail = b.error ?? b.detail ?? detail;
    } catch {
      /* corpo não-JSON: mantém statusText */
    }
    throw new Error(i18n.t("apiErrors.previewUnavailable", { status: res.status, detail }).trim());
  }
  return res.blob();
}

// GET /v1/voice/call/session — "ticket" da emulação de ligação: a URL do
// WebSocket do talk já com o token embutido. O browser conecta DIRETO no talk
// (sem proxy de áudio pelo back). Restrito a director/manager no back.
export interface VoiceCallSession {
  wsUrl: string;
}
export async function getVoiceCallSession(): Promise<VoiceCallSession> {
  const { data, error } = await api<VoiceCallSession>("/v1/voice/call/session");
  if (error) throw toApiError("GET /v1/voice/call/session", error);
  return data;
}
